import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, AlertTriangle, Phone, Heart } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

export function ImportantNoticePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img 
              src="https://i.postimg.cc/mzV1rvRQ/logopareiad001.png" 
              alt="U-Clean" 
              className="h-8"
            />
            <span className="font-bold text-xl text-gray-800">U-Clean</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Aviso Importante</h1>
          <p className="text-gray-500">Leia com atenção antes de utilizar a plataforma</p>
        </div>

        {/* Main Warning Card */}
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-8 w-8 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-amber-900 mb-3">
                  A U-Clean NÃO substitui atendimento médico ou emergencial
                </h2>
                <p className="text-amber-800 leading-relaxed">
                  A U-Clean é uma plataforma de apoio, acolhimento e desenvolvimento pessoal. 
                  <strong> Nossos conteúdos, ferramentas e comunidade têm caráter informativo, 
                  educativo e complementar.</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What U-Clean IS NOT */}
        <Card className="mb-6">
          <CardContent className="p-6 md:p-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              O que a U-Clean <span className="text-red-600">NÃO É</span>:
            </h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <span className="text-red-500 font-bold">✗</span>
                <span>Substituto para atendimento médico, psiquiátrico ou hospitalar</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 font-bold">✗</span>
                <span>Serviço de emergência ou pronto-socorro</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 font-bold">✗</span>
                <span>Substituto para terapia ou tratamento profissional</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 font-bold">✗</span>
                <span>Garantia de cura ou resultado específico</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 font-bold">✗</span>
                <span>Linha de crise ou prevenção ao suicídio</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* What U-Clean IS */}
        <Card className="mb-6">
          <CardContent className="p-6 md:p-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              O que a U-Clean <span className="text-green-600">É</span>:
            </h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <span className="text-green-500 font-bold">✓</span>
                <span>Uma comunidade de apoio mútuo entre pessoas em recuperação</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 font-bold">✓</span>
                <span>Plataforma com conteúdos educativos e informativos</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 font-bold">✓</span>
                <span>Ferramenta complementar ao seu tratamento</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 font-bold">✓</span>
                <span>Espaço para conexão com profissionais credenciados</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 font-bold">✓</span>
                <span>Sistema de gamificação para motivação na jornada</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Emergency Card */}
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <Phone className="h-8 w-8 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-red-900 mb-3">
                  Em caso de emergência
                </h3>
                <p className="text-red-800 mb-4">
                  Se você ou alguém está em situação de crise, risco à vida, urgência médica 
                  ou emergência psiquiátrica, procure <strong>imediatamente</strong>:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                    <span className="font-bold text-red-700 text-lg">192</span>
                    <span className="text-gray-700">SAMU - Serviço de Atendimento Móvel de Urgência</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                    <span className="font-bold text-red-700 text-lg">188</span>
                    <span className="text-gray-700">CVV - Centro de Valorização da Vida</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                    <span className="font-bold text-red-700 text-lg">190</span>
                    <span className="text-gray-700">Polícia Militar</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                    <span className="font-bold text-red-700 text-lg">193</span>
                    <span className="text-gray-700">Corpo de Bombeiros</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Encouragement Card */}
        <Card className="mb-8 border-green-200 bg-green-50">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <Heart className="h-8 w-8 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-green-900 mb-3">
                  Você não está sozinho
                </h3>
                <p className="text-green-800 leading-relaxed">
                  A jornada de recuperação é desafiadora, mas possível. A U-Clean existe para 
                  te apoiar nesse caminho, conectando você a uma comunidade que entende suas 
                  dificuldades. Lembre-se: buscar ajuda profissional é um sinal de força, não de fraqueza.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Related Links */}
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          <Link to="/termos">
            <Button variant="outline">Termos de Uso</Button>
          </Link>
          <Link to="/privacidade">
            <Button variant="outline">Política de Privacidade</Button>
          </Link>
        </div>

        {/* Back Button */}
        <div className="text-center">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 border-t py-6 mt-8">
        <div className="container max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} U-Clean. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}

export default ImportantNoticePage;
